import type {
  ApiError as SharedApiError,
  ApiResponse,
  User,
  Photo,
  PhotoListItem,
  PhotoMetadata,
  PhotoUrls,
  PhotoDetail,
  Friend,
  FriendWithPhotoCount,
  LogFile,
  PaginatedResponse,
  StorageInfo,
  Album,
  AlbumListItem,
  AlbumDetail,
  AlbumPhoto,
  CreateAlbumRequest,
  UpdateAlbumRequest,
  AddPhotosToAlbumRequest,
  ReorderAlbumPhotosRequest,
  Share,
  ShareWithUrl,
  ShareWithPhoto,
  CreateShareRequest,
  ShareListResponse,
  LinkedAccountsResponse,
  OAuthProvider,
  UpdatePrimaryEmailRequest,
  Release,
  ReleasesResponse,
  PresignUploadFileRequest,
  PresignUploadRequest,
  PresignUploadResponse,
  ConfirmUploadRequest,
  ConfirmUploadResponse,
} from '@omokage/shared';

// Re-export shared types for convenience
export type {
  ApiResponse,
  User,
  Photo,
  PhotoListItem,
  PhotoMetadata,
  PhotoUrls,
  PhotoDetail,
  Friend,
  FriendWithPhotoCount,
  LogFile,
  PaginatedResponse,
  StorageInfo,
  Album,
  AlbumListItem,
  AlbumDetail,
  AlbumPhoto,
  CreateAlbumRequest,
  UpdateAlbumRequest,
  AddPhotosToAlbumRequest,
  ReorderAlbumPhotosRequest,
  Share,
  ShareWithUrl,
  ShareWithPhoto,
  CreateShareRequest,
  ShareListResponse,
  LinkedAccountsResponse,
  OAuthProvider,
  UpdatePrimaryEmailRequest,
  Release,
  ReleasesResponse,
  PresignUploadFileRequest,
  PresignUploadRequest,
  PresignUploadResponse,
  ConfirmUploadRequest,
  ConfirmUploadResponse,
};

// API Error class with status code (extends shared type structure)
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Convert to shared ApiError format
   */
  toSharedFormat(): SharedApiError {
    return {
      error: {
        code: this.code || `HTTP_${this.status}`,
        message: this.message,
        details: this.details,
      },
    };
  }
}

// API Client configuration
const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Request options type
interface RequestOptions {
  params?: Record<string, string | number | boolean>;
}

// Request config for interceptors
interface RequestConfig {
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string;
}

// Interceptor types
type RequestInterceptor = (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;
type ResponseInterceptor = (response: Response) => Response | Promise<Response>;
type ErrorInterceptor = (error: ApiError) => ApiError | Promise<ApiError>;

// Interceptor storage
const requestInterceptors: RequestInterceptor[] = [];
const responseInterceptors: ResponseInterceptor[] = [];
const errorInterceptors: ErrorInterceptor[] = [];

/**
 * Add a request interceptor
 * @returns Function to remove the interceptor
 */
export function addRequestInterceptor(interceptor: RequestInterceptor): () => void {
  requestInterceptors.push(interceptor);
  return () => {
    const index = requestInterceptors.indexOf(interceptor);
    if (index > -1) requestInterceptors.splice(index, 1);
  };
}

/**
 * Add a response interceptor
 * @returns Function to remove the interceptor
 */
export function addResponseInterceptor(interceptor: ResponseInterceptor): () => void {
  responseInterceptors.push(interceptor);
  return () => {
    const index = responseInterceptors.indexOf(interceptor);
    if (index > -1) responseInterceptors.splice(index, 1);
  };
}

/**
 * Add an error interceptor
 * @returns Function to remove the interceptor
 */
export function addErrorInterceptor(interceptor: ErrorInterceptor): () => void {
  errorInterceptors.push(interceptor);
  return () => {
    const index = errorInterceptors.indexOf(interceptor);
    if (index > -1) errorInterceptors.splice(index, 1);
  };
}

// Build URL with query parameters
function buildUrl(path: string, params?: Record<string, string | number | boolean>): string {
  const url = `${BASE_URL}${path}`;

  if (!params) {
    return url;
  }

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `${url}?${queryString}` : url;
}

// Get default headers for API requests
// Auth is handled via HttpOnly cookies automatically by the browser
function getDefaultHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
  };
}

// Apply request interceptors
async function applyRequestInterceptors(config: RequestConfig): Promise<RequestConfig> {
  let result = config;
  for (const interceptor of requestInterceptors) {
    result = await interceptor(result);
  }
  return result;
}

// Apply response interceptors
async function applyResponseInterceptors(response: Response): Promise<Response> {
  let result = response;
  for (const interceptor of responseInterceptors) {
    result = await interceptor(result);
  }
  return result;
}

// Apply error interceptors
async function applyErrorInterceptors(error: ApiError): Promise<ApiError> {
  let result = error;
  for (const interceptor of errorInterceptors) {
    result = await interceptor(result);
  }
  return result;
}

// Token refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Attempt to refresh the access token using the refresh token
 * Returns true if refresh was successful, false otherwise
 */
async function refreshAccessToken(): Promise<boolean> {
  // If already refreshing, wait for the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Include HttpOnly cookies
      });

      if (response.ok) {
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Error response type guard
interface ErrorResponse {
  error?: { message?: string; code?: string; details?: unknown };
  message?: string;
  code?: string;
  details?: unknown;
}

function isErrorResponse(data: unknown): data is ErrorResponse {
  return typeof data === 'object' && data !== null && ('error' in data || 'message' in data);
}

// Handle response (without 401 retry - used internally)
async function handleResponseInternal<T>(response: Response): Promise<T> {
  // Apply response interceptors
  const interceptedResponse = await applyResponseInterceptors(response);

  // Parse response body
  let data: unknown;
  try {
    data = await interceptedResponse.json();
  } catch {
    // Response has no body (e.g., 204 No Content)
    data = null;
  }

  // Throw error for non-2xx responses
  if (!interceptedResponse.ok) {
    let errorMessage = `HTTP ${interceptedResponse.status}: ${interceptedResponse.statusText}`;
    let errorCode: string | undefined;
    let errorDetails: unknown;

    // Safely extract error information using runtime type check
    if (isErrorResponse(data)) {
      const errorInfo = data.error || data;
      errorMessage = errorInfo.message || errorMessage;
      errorCode = errorInfo.code;
      errorDetails = errorInfo.details;
    }

    const error = new ApiError(interceptedResponse.status, errorMessage, errorCode, errorDetails);
    throw await applyErrorInterceptors(error);
  }

  return data as T;
}

// Execute request with interceptors and automatic token refresh on 401
async function executeRequest<T>(config: RequestConfig, isRetry = false): Promise<T> {
  // Apply request interceptors
  const interceptedConfig = await applyRequestInterceptors(config);

  let response: Response;
  try {
    response = await fetch(interceptedConfig.url, {
      method: interceptedConfig.method,
      headers: interceptedConfig.headers,
      body: interceptedConfig.body,
      credentials: 'include', // Include cookies for authentication
    });
  } catch (error) {
    // Network error or fetch failure
    const networkError = new ApiError(
      0,
      error instanceof Error ? error.message : 'Network request failed',
      'NETWORK_ERROR'
    );
    throw await applyErrorInterceptors(networkError);
  }

  // Handle 401 Unauthorized - attempt token refresh once
  if (response.status === 401 && !isRetry) {
    // Skip refresh only for refresh/logout endpoints to prevent infinite loops
    // /auth/me and other /auth/ endpoints should still trigger refresh
    const isRefreshOrLogout =
      interceptedConfig.url.includes('/auth/refresh') ||
      interceptedConfig.url.includes('/auth/logout');

    if (!isRefreshOrLogout) {
      const refreshed = await refreshAccessToken();

      if (refreshed) {
        // Retry the original request after successful refresh
        return executeRequest<T>(config, true);
      }
    }
  }

  return handleResponseInternal<T>(response);
}

// API Client
export const apiClient = {
  // GET request
  async get<T = unknown>(path: string, options?: RequestOptions): Promise<T> {
    const config: RequestConfig = {
      url: buildUrl(path, options?.params),
      method: 'GET',
      headers: getDefaultHeaders(),
    };
    return executeRequest<T>(config);
  },

  // POST request
  async post<T = unknown>(path: string, body?: unknown): Promise<T> {
    const config: RequestConfig = {
      url: buildUrl(path),
      method: 'POST',
      headers: getDefaultHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    };
    return executeRequest<T>(config);
  },

  // PUT request
  async put<T = unknown>(path: string, body?: unknown): Promise<T> {
    const config: RequestConfig = {
      url: buildUrl(path),
      method: 'PUT',
      headers: getDefaultHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    };
    return executeRequest<T>(config);
  },

  // PATCH request
  async patch<T = unknown>(path: string, body?: unknown): Promise<T> {
    const config: RequestConfig = {
      url: buildUrl(path),
      method: 'PATCH',
      headers: getDefaultHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    };
    return executeRequest<T>(config);
  },

  // DELETE request
  async delete<T = unknown>(path: string, body?: unknown): Promise<T> {
    const config: RequestConfig = {
      url: buildUrl(path),
      method: 'DELETE',
      headers: getDefaultHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    };
    return executeRequest<T>(config);
  },

  /**
   * Request presigned upload URLs for photos
   * @param files Array of file metadata to get upload URLs for
   * @returns Presigned URLs and R2 keys for uploading
   */
  async presignUpload(files: PresignUploadFileRequest[]): Promise<PresignUploadResponse> {
    const request: PresignUploadRequest = { files };
    return this.post<PresignUploadResponse>('/photos/presign', request);
  },

  /**
   * Upload a file directly to R2 using presigned URL
   * @param url Presigned URL to upload to
   * @param file Blob/File to upload
   * @param contentType MIME type of the file
   * @param onProgress Optional progress callback (0-100)
   */
  async uploadToR2(
    url: string,
    file: Blob,
    contentType: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded * 100) / e.total);
            onProgress(percent);
          }
        });
      }

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`R2 upload failed with status ${xhr.status}`));
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error('Network error during R2 upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('R2 upload aborted'));
      });

      // Send PUT request to presigned URL
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);
    });
  },

  /**
   * Confirm photo uploads after R2 upload completes
   * @param confirmations Array of photo IDs with optional metadata
   * @returns Upload confirmation results
   */
  async confirmUpload(confirmations: ConfirmUploadRequest): Promise<ConfirmUploadResponse> {
    return this.post<ConfirmUploadResponse>('/photos/confirm', confirmations);
  },
};
